import React, { useState, useRef } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CloseIcon,
  CompareArrowsIcon,
  EditIcon,
  FormatAlignLeftIcon,
  VisibilityIcon,
} from "../../../icons";
import Editor, { DiffEditor } from "@monaco-editor/react";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export type CodeLanguage =
  | "json"
  | "graphql"
  | "yaml"
  | "html"
  | "javascript"
  | "typescript"
  | "sql"
  | "markdown"
  | "nunjucks"
  | "plaintext";

export type InputCodeEditorProps = {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  language?: CodeLanguage;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  enableValidation?: boolean;
  enableFormat?: boolean;
  enableDiff?: boolean;
  originalValue?: string;
  minHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
};

const formatCode = (code: string, language: CodeLanguage): string => {
  try {
    switch (language) {
      case "json":
        return JSON.stringify(JSON.parse(code), null, 2);
      case "yaml":
      case "graphql":
      case "sql":
      case "html":
      case "javascript":
      case "typescript":
      case "plaintext":
      default:
        return code;
    }
  } catch (e) {
    return code;
  }
};

const validateCode = (code: string, language: CodeLanguage): string | null => {
  try {
    switch (language) {
      case "json":
        JSON.parse(code);
        return null;
      case "yaml":
      case "graphql":
      case "sql":
      case "html":
      case "javascript":
      case "typescript":
      case "plaintext":
      default:
        return null;
    }
  } catch (e: any) {
    return e.message || "Error de validación";
  }
};

export const InputCodeEditor: React.FC<InputCodeEditorProps> = observer(
  ({
    label,
    value,
    onChange,
    language = "plaintext",
    error = false,
    helperText = "",
    readOnly = false,
    enableValidation = true,
    enableFormat = true,
    enableDiff = false,
    originalValue,
    minHeight = 100,
    maxHeight = 200,
    disabled = false,
    hidden = false,
    description,
  }) => {
    const theme = useTheme();
    if (hidden) return null;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editorContent, setEditorContent] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showDiff, setShowDiff] = useState(false);
    const editorRef = useRef<any>(null);

    const normalizeValue = (val: any): string => {
      if (!val) return "";
      if (typeof val === "string") return val;
      if (typeof val === "object") {
        try {
          return JSON.stringify(val, null, 2);
        } catch (e) {
          return String(val);
        }
      }
      return String(val);
    };

    const handleOpenDialog = () => {
      const normalizedValue = normalizeValue(value);
      setEditorContent(normalizedValue);
      setDialogOpen(true);
      setShowDiff(false);
      if (enableValidation && normalizedValue) {
        const validationResult = validateCode(normalizedValue, language);
        setValidationError(validationResult);
      }
    };

    const handleCloseDialog = () => {
      setDialogOpen(false);
      setEditorContent("");
      setValidationError(null);
      setShowDiff(false);
    };

    const handleSave = () => {
      if (onChange) {
        onChange(editorContent);
      }
      handleCloseDialog();
    };

    const handleEditorChange = (newValue: string | undefined) => {
      const content = newValue || "";
      setEditorContent(content);

      if (enableValidation && content) {
        const validationResult = validateCode(content, language);
        setValidationError(validationResult);
      } else {
        setValidationError(null);
      }
    };

    const handleFormat = () => {
      const formatted = formatCode(editorContent, language);
      setEditorContent(formatted);
      if (editorRef.current) {
        editorRef.current.setValue(formatted);
      }
    };

    const handleToggleDiff = () => {
      setShowDiff(!showDiff);
    };

    const getMonacoLanguage = (lang: CodeLanguage): string => {
      const languageMap: Record<CodeLanguage, string> = {
        json: "json",
        graphql: "graphql",
        yaml: "yaml",
        html: "html",
        javascript: "javascript",
        typescript: "typescript",
        sql: "sql",
        markdown: "markdown",
        nunjucks: "twig",
        plaintext: "plaintext",
      };
      return languageMap[lang] || "plaintext";
    };

    const renderPreview = () => {
      const displayValue = normalizeValue(value) || "Sin contenido";
      const isValid =
        !enableValidation ||
        !value ||
        validateCode(displayValue, language) === null;

      return (
        <Box
          sx={{
            border: error
              ? "1px solid " + theme.palette.error.main
              : "1px solid " + theme.palette.divider,
            borderRadius: 1,
            p: 1,
            minHeight,
            maxHeight,
            overflow: "auto",
            backgroundColor: readOnly
              ? theme.palette.action.hover
              : theme.palette.background.paper,
            fontFamily: "monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            color: isValid ? "inherit" : theme.palette.error.main,
          }}
        >
          {displayValue}
        </Box>
      );
    };

    const canShowDiff = enableDiff && originalValue && originalValue !== value;

    return (
      <Box sx={{ width: "100%", m: 1 }}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          component="div"
        >
          <FieldLabelWithHelp
            label={
              <>
                {label} {language && `(${language.toUpperCase()})`}
              </>
            }
            description={description}
          />
        </Typography>

        {renderPreview()}

        {helperText && (
          <Typography
            variant="caption"
            color={error ? "error" : "textSecondary"}
            sx={{ ml: 1 }}
          >
            {helperText}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            startIcon={readOnly ? <VisibilityIcon /> : <EditIcon />}
            onClick={handleOpenDialog}
          >
            {readOnly ? "Ver código" : `Editar ${language.toUpperCase()}`}
          </Button>
        </Stack>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">
                {readOnly ? "Ver" : "Editar"} {label}
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
              </Alert>
            )}

            <Box
              sx={{
                height: 500,
                border: "1px solid " + theme.palette.divider,
                borderRadius: 1,
              }}
            >
              {showDiff && originalValue ? (
                <DiffEditor
                  height="100%"
                  language={getMonacoLanguage(language)}
                  original={originalValue}
                  modified={editorContent}
                  options={{
                    readOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    renderSideBySide: true,
                  }}
                  onMount={(editor) => {
                    editorRef.current = editor;
                  }}
                />
              ) : (
                <Editor
                  height="100%"
                  language={getMonacoLanguage(language)}
                  value={editorContent}
                  onChange={handleEditorChange}
                  options={{
                    readOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    lineNumbers: "on",
                    wordWrap: "on",
                    automaticLayout: true,
                  }}
                  onMount={(editor) => {
                    editorRef.current = editor;
                  }}
                />
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flex: 1, justifyContent: "space-between" }}
            >
              <Stack direction="row" spacing={1}>
                {!readOnly && enableFormat && (
                  <Button
                    size="small"
                    startIcon={<FormatAlignLeftIcon />}
                    onClick={handleFormat}
                    disabled={!!validationError}
                  >
                    Formatear
                  </Button>
                )}
                {canShowDiff && (
                  <Button
                    size="small"
                    startIcon={<CompareArrowsIcon />}
                    onClick={handleToggleDiff}
                  >
                    {showDiff ? "Editor" : "Comparar"}
                  </Button>
                )}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button onClick={handleCloseDialog}>
                  {readOnly ? "Cerrar" : "Cancelar"}
                </Button>
                {!readOnly && (
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={!!validationError}
                  >
                    Guardar
                  </Button>
                )}
              </Stack>
            </Stack>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);
